import Breadcrumbs from "@/components/Breadcrumbs";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import { TemplateService } from "@/services/admin-services";
import { Template } from "@/services/admin-services/models";
import { PlusIcon } from "@heroicons/react/24/outline";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const breadcrumbs = [
    {
        to: "..",
        title: "Administration",
    },
    {
        title: "Templates",
        to: "",
    },
    {
        title: "Saved Templates",
        to: "",
    },
];

const Overview = () => {
    const [templates, setTemplates] = useState<Array<Template>>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [deleteTemplate, setDeleteTemplate] = useState<string | null>(null);
    const [showWarning, setShowWarning] = useState<boolean>(false);
    const [clone, setClone] = useState<string | null>(null);

    const navigate = useNavigate();

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    const templateService = new TemplateService("int");

    useEffect(() => {
        templateService.getTemplates().then((templates) => {
            setTemplates(templates);
            setLoading(false);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onClone = (id: string) => {
        setClone(id);

        templateService.getTemplate(id).then((template) => {
            if (!template) return;

            const timestamp = new Date().toLocaleString();

            templateService.createTemplate(`${template.name} (Copy - ${timestamp})`).then((newTemplate) => {
                const promises: Array<Promise<void>> = [];
                template.availableLanguages.forEach((language) => {
                    const promise = templateService.getTemplateContent(id, language).then((data) => {
                        if (!data) return;
                        templateService.createTemplateContent(newTemplate.id, language, data);
                    });

                    promises.push(promise);
                });

                Promise.all(promises).then(() => {
                    templateService.getTemplates().then((templates) => {
                        setTemplates(templates);
                        setClone(null);
                    });
                });
            });
        });
    };

    const onDelete = (id: string) => setDeleteTemplate(id);

    const onCancelDelete = () => setDeleteTemplate(null);

    const onPerformDelete = () => {
        templateService.deleteTemplate(deleteTemplate!).then((success) => {
            setDeleteTemplate(null);

            if (success) {
                const templatesSet = templates.filter((x) => x.id !== deleteTemplate!);
                setTemplates(templatesSet);
            } else {
                setShowWarning(true);
            }
        });
    };

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbs} />

            <Divider />

            <Grid container spacing={4} p={4}>
                <Grid size={{ xs: 12, md: 12 }} sx={{ display: "flex" }}>
                    <Button
                        component={Link}
                        to="new"
                        variant="contained"
                        startIcon={<PlusIcon className="h-5 w-5" />}
                        sx={{ ml: "auto" }}
                    >
                        Create New Template
                    </Button>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 750 }} aria-label="simple table">
                            <TableHead sx={{ bgcolor: "alternate.main" }}>
                                <TableRow>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Name
                                        </Typography>
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={9}
                                            sx={{
                                                height: 400,
                                                alignItems: "center",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", placeContent: "center" }}>
                                                <CircularProgress />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    templates.map((item, i) => (
                                        <TableRow key={i} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                            <TableCell component="th" scope="row" sx={{ verticalAlign: "top" }}>
                                                <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                    {item.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ p: 0 }}>
                                                <Box sx={{ display: "flex" }}>
                                                    <Button
                                                        color={"primary"}
                                                        sx={{ ml: "auto" }}
                                                        onClick={() => navigate(item.id)}
                                                        disabled={Boolean(clone)}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Box sx={{ ml: 2, position: "relative" }}>
                                                        <Button
                                                            color={"primary"}
                                                            onClick={() => onClone(item.id)}
                                                            disabled={Boolean(clone)}
                                                        >
                                                            Clone
                                                        </Button>
                                                        {clone === item.id && (
                                                            <CircularProgress
                                                                size={24}
                                                                sx={{
                                                                    position: "absolute",
                                                                    top: "50%",
                                                                    left: "50%",
                                                                    marginTop: "-12px",
                                                                    marginLeft: "-12px",
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                    <Button
                                                        color={"error"}
                                                        sx={{ ml: 2, mr: 1 }}
                                                        onClick={() => onDelete(item.id)}
                                                        disabled={Boolean(clone)}
                                                    >
                                                        Delete
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>

            {deleteTemplate && (
                <ConfirmDeleteDialog
                    open={Boolean(deleteTemplate)}
                    title={"Delete template"}
                    message={"Are you sure you wish to delete this template? You can not undo this action!"}
                    onCancel={onCancelDelete}
                    onDelete={onPerformDelete}
                />
            )}

            <Dialog
                open={showWarning}
                onClose={() => setShowWarning(false)}
                fullWidth
                maxWidth={"sm"}
                fullScreen={fullScreen}
            >
                <DialogTitle>Unable to delete template</DialogTitle>
                <DialogContent dividers>
                    <DialogContentText>
                        We were unable to delete this template because it is being used by one or more element pages.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setShowWarning(false)}>
                        Ok
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Overview;
